import { MessageDescriptor } from 'react-intl';
import {
  walletErDetailPageMessages,
  walletPageMessages,
  walletProductTransactionReasonMessages,
} from '../../../../messages';
import commonErrorMessages from '../../../../messages/common-messages/common-error.messages';
import walletCategoriesMessages from '../../../../messages/wallet/wallet-categories.messages';
import {
  Dossier,
  DossierFilter,
  DownloaderMediaType,
  FileDownloadedType,
  ProductCategory,
  ProductCategoryType,
  ProductDetails,
  ProductMacroCategorySummaryType,
  ProductMacroCategoryType,
  ProductPerformanceDateFilterType,
  SubDossier,
  TransactionReasonType,
} from '../../../../model';
import { ErrorMessages } from '../../../../model/error';
import { WalletError } from './my-wallet-types';
import { RiskProfileCheckType } from '../../../../model/wallet/wallet-ow';

export const messagesForWalletError = (error: WalletError): ErrorMessages => {
  switch (error) {
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const messagesForPrevinetError = (error: WalletError): ErrorMessages => {
  switch (error) {
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: walletPageMessages.errorDescriptionAccessPrevinet,
      };
  }
};

export const labelForDetailsSection = (): MessageDescriptor => {
  return walletPageMessages.detailsSectionLabel;
}

export const labelForWalletMacroCategoryType = (
  macrocategory: ProductMacroCategoryType
): MessageDescriptor => {
  switch (macrocategory) {
    case 'FinancialInsurance':
      return walletCategoriesMessages.macrocategoryFinancialInsurance;
    case 'Administered':
      return walletCategoriesMessages.macrocategoryAdministered;
    case 'Liquidity':
      return walletCategoriesMessages.macrocategoryLiquidity;
    case 'Welfare':
      return walletCategoriesMessages.macrocategoryWelfare;
    case 'Managed':
      return walletCategoriesMessages.macrocategoryManaged;
    case 'RealEconomy':
      return walletCategoriesMessages.macrocategoryRealEconomy;
    case 'TraditionalInsurance':
      return walletCategoriesMessages.macrocategoryTraditionalInsurance;
    case 'TotalFia':
      return walletCategoriesMessages.macrocategoryTotalFia;
    case 'TotalPortfolio':
      return walletCategoriesMessages.macrocategoryTotalPortfolio;
    default:
      return {
        id: macrocategory,
        defaultMessage: macrocategory,
      };
  }
};

export const labelForPortfolioSummaryMacroCategoryType = (
  code: string,
  description: string
): MessageDescriptor => {
  switch (code) {
    case '001':
      return walletCategoriesMessages.macrocategoryManaged;
    case '002':
      return walletCategoriesMessages.macrocategoryAdministered;
    case '003':
      return walletCategoriesMessages.macrocategoryServices;
    case '004':
      return walletCategoriesMessages.macrocategoryLiquidity;
    case '005':
      return walletCategoriesMessages.macrocategoryLiabilities;
    case '006':
      return walletCategoriesMessages.macrocategoryFinancialInsurance;
    case '007':
      return walletCategoriesMessages.macrocategoryTraditionalInsurance;
    case '011':
      return walletCategoriesMessages.macrocategoryRealEconomy;
    case '012':
      return walletCategoriesMessages.macrocategoryWelfare;
    case 'TraditionalInsurance':
      return walletCategoriesMessages.macrocategoryTraditionalInsurance;
    case '':

    default:
      return {
        id: code,
        defaultMessage: description,
      };
  }
};

export const labelsForRiskProfileTable = (type: RiskProfileCheckType) => {
  switch (type) {
    case 'marketRisk':
      return {
        name: walletErDetailPageMessages.riskProfileMarketRiskName,
        description:
          walletErDetailPageMessages.riskProfileMarketRiskDescription,
      };
    case 'creditRisk':
      return {
        name: walletErDetailPageMessages.riskProfileCreditRiskName,
        description:
          walletErDetailPageMessages.riskProfileCreditRiskDescription,
      };
    case 'investableResidual':
      return {
        name: walletErDetailPageMessages.riskProfileInvestableResidualName,
        description:
          walletErDetailPageMessages.riskProfileInvestableResidualDescription,
      };
    case 'complex':
      return {
        name: walletErDetailPageMessages.riskProfileComplexName,
        description: walletErDetailPageMessages.riskProfileComplexDescription,
      };
    case 'illiquidity':
      return {
        name: walletErDetailPageMessages.riskProfileIlliquidityName,
        description:
          walletErDetailPageMessages.riskProfileIlliquidityDescription,
      };
    case 'esg':
      return {
        name: walletErDetailPageMessages.riskProfileEsgName,
        description: walletErDetailPageMessages.riskProfileEsgDescription,
      };
  }
};

export const labelForQuoteEmissione = () => {
  return walletCategoriesMessages.quoteEmissione;
};

export const labelForCommitment = () => {
  return walletCategoriesMessages.commitment;
};

export const labelForRealEconomyCta = () => {
  return walletCategoriesMessages.goToDetail;
};

export const labelForRealEconomyExplain = () => {
  return walletCategoriesMessages.explain;
};

export const shortLabelForWalletMacroCategoryType = (
  macrocategory: ProductMacroCategoryType
): MessageDescriptor => {
  switch (macrocategory) {
    case 'RealEconomy':
      return walletCategoriesMessages.macrocategoryRealEconomyShort;
    case 'FinancialInsurance':
      return walletCategoriesMessages.macrocategoryFinancialInsuranceShort;
    case 'TraditionalInsurance':
      return walletCategoriesMessages.macrocategoryTraditionalInsuranceShort;
    case "TotalFia":
      return  walletCategoriesMessages.macrocategoryTotalFiaShort;
    case "TotalPortfolio":
      return  walletCategoriesMessages.macrocategoryTotalPortfolioShort;
    default:
      return labelForWalletMacroCategoryType(macrocategory);
  }
};

export const shortLabelForWalletMacroCategoryList = (
  macrocategory: ProductMacroCategoryType
): MessageDescriptor => {
  switch (macrocategory) {
    case 'FinancialInsurance':
      return walletCategoriesMessages.macrocategoryFinancialInsuranceListShort;
    default:
      return labelForWalletMacroCategoryType(macrocategory);
  }
};

export const labelForWalletMacroCategorySummaryType = (
  macrocategory: ProductMacroCategorySummaryType
): MessageDescriptor => {
  switch (macrocategory) {
    case 'ManagedFinancialInsurance':
      return walletCategoriesMessages.macrocategoryManagedFinancialInsurance;
    case 'Administered':
      return walletCategoriesMessages.macrocategoryAdministered;
    case 'Liquidity':
      return walletCategoriesMessages.macrocategoryLiquidity;
    case 'Welfare':
      return walletCategoriesMessages.macrocategoryWelfare;
    case 'RealEconomy':
      return walletCategoriesMessages.macrocategoryRealEconomy;
    case 'FIA':
      return walletCategoriesMessages.macrocategoryFIA;
    default:
      return {
        id: macrocategory,
        defaultMessage: macrocategory,
      };
  }
};

export const labelForWalletCategoryType = (
  category: ProductCategoryType
): MessageDescriptor => {
  switch (category) {
    case 'AssetManagement':
      return walletCategoriesMessages.categoryAssetManagement;
    case 'BankAccounts':
      return walletCategoriesMessages.categoryBankAccounts;
    case 'Bonds':
      return walletCategoriesMessages.categoryBonds;
    case 'BondsAzimut':
      return walletCategoriesMessages.categoryBondsAzimut;
    case 'Certificates':
      return walletCategoriesMessages.categoryCertificates;
    case 'Derivative':
      return walletCategoriesMessages.categoryDerivative;
    case 'ETF':
      return walletCategoriesMessages.categoryETF;
    case 'FundsManagement':
      return walletCategoriesMessages.categoryFundsManagement;
    case 'Hedge Fund':
      return walletCategoriesMessages.categoryHedgeFund;
    case 'LiquidAlternative':
      return walletCategoriesMessages.categoryLiquidAlternative;
    case 'MutualFunds':
      return walletCategoriesMessages.categoryMutualFunds;
    case 'PensionFund':
      return walletCategoriesMessages.categoryPensionFund;
    case 'PrivateDebt':
      return walletCategoriesMessages.categoryPrivateDebt;
    case 'PrivateEquity':
      return walletCategoriesMessages.categoryPrivateEquity;
    case 'RealAsset':
      return walletCategoriesMessages.categoryRealAsset;
    case 'SICAV':
      return walletCategoriesMessages.categorySICAV;
    case 'Shares':
      return walletCategoriesMessages.categoryShares;
    case 'TraditionalLifeInsurance':
      return walletCategoriesMessages.categoryTraditionalLifeInsurance;
    case 'TraditionalPoliciesRevalued':
      return walletCategoriesMessages.categoryTraditionalPoliciesRevalued;
    case 'UnitLinked':
      return walletCategoriesMessages.categoryUnitLinked;
    case 'UnitLinkedAtPrice':
      return walletCategoriesMessages.categoryUnitLinkedAtPrice;
    case 'VentureCapital':
      return walletCategoriesMessages.categoryVentureCapital;
    default:
      return {
        id: category,
        defaultMessage: category,
      };
  }
};

export const labelForWalletReasonType = (
  macrocategory: TransactionReasonType
): MessageDescriptor => {
  switch (macrocategory) {
    case 'InitialDeposit':
      return walletProductTransactionReasonMessages.Decrement;
    case 'SwitchToStart':
      return walletProductTransactionReasonMessages.switchToStart;
    case 'NextDeposit':
      return walletProductTransactionReasonMessages.nextDeposit;
    case 'PartialRefund':
      return walletProductTransactionReasonMessages.PartialRefund;
    case 'TotalRefund':
      return walletProductTransactionReasonMessages.TotalRefund;
    case 'PartialSwitchOut':
      return walletProductTransactionReasonMessages.PartialRefund;
    case 'TotalSwitchOut':
      return walletProductTransactionReasonMessages.TotalSwitchOut;
    case 'SwitchToNext':
      return walletProductTransactionReasonMessages.SwitchToNext;
    case 'Increment':
      return walletProductTransactionReasonMessages.Increment;
    case 'Decrement':
      return walletProductTransactionReasonMessages.Decrement;
    case 'RefoundVsPartialInsurancePolicy':
      return walletProductTransactionReasonMessages.RefoundVsPartialInsurancePolicy;
    case 'RefoundVsTotalInsurancePolicy':
      return walletProductTransactionReasonMessages.RefoundVsTotalInsurancePolicy;
    case 'PeriodicNextDeposit':
      return walletProductTransactionReasonMessages.PeriodicNextDeposit;
    default:
      return {
        id: macrocategory,
        defaultMessage: macrocategory,
      };
  }
};

export const mapMacroCategoriesToMacroCategorie = (
  macroCategory: ProductMacroCategoryType
):
  | 'GESTITO_ASSICURATIVO_FINANZIARIO'
  | 'AMMINISTRATO'
  | 'ECONOMIA_REALE'
  | 'PREVIDENZA' => {
  switch (macroCategory) {
    case 'FinancialInsurance':
    case 'Managed':
      return 'GESTITO_ASSICURATIVO_FINANZIARIO';
    case 'Administered':
      return 'AMMINISTRATO';
    case 'Welfare':
      return 'PREVIDENZA';
    case 'RealEconomy':
      return 'ECONOMIA_REALE';
    default:
      return null;
  }
};

export const mapDateTypeToPeriodo = (
  dateType: ProductPerformanceDateFilterType
): 'DA_INIZIO_ANNO' | 'ULTIMO_ANNO' | 'DA_APERTURA' | 'INTERVALLO' => {
  switch (dateType) {
    case 'START_YEAR':
      return 'DA_INIZIO_ANNO';
    case 'LAST_YEAR':
      return 'ULTIMO_ANNO';
    case 'DA_APERTURA':
      return 'DA_APERTURA';
    default:
      return null;
  }
};

const condition = (dossier: Dossier | SubDossier): string[] =>
  dossier.flagSelected && !(dossier as Dossier).subDossier
    ? [(dossier as SubDossier).contract || (dossier as Dossier).code]
    : [];

export const getDossierFilterContracts = (
  dossierFilter: DossierFilter
): string[] => {
  return dossierFilter?.dossier?.flatMap((item) =>
    item.subDossier ? item.subDossier.flatMap(condition) : condition(item)
  );
};

export const getDossierFilterCode = (
  dossierFilter: DossierFilter
): string[] => {
  return dossierFilter.dossier.map((dos) => dos.code);
};

export const getFilenameTransactionsUnderlying = (
  { company, group, subProduct, contract, ISIN, RP }: ProductDetails,
  { type }: ProductCategory,
  req: { mediaType: DownloaderMediaType; type: FileDownloadedType }
): string => {
  const categoryGP: ProductCategoryType[] = [
    'AssetManagement',
    'FundsManagement',
  ];
  const base =
    req.type === 'transaction' ? 'portfolio_transaction' : 'portfolio_details';
  const fixedValue = categoryGP.includes(type)
    ? `${RP.split('_')[0]}_`
    : 'RT_A_CR_';
  const secondFixedValue = categoryGP.includes(type) ? '' : `${contract}_PR_`;
  const id = categoryGP.includes(type) ? RP.split('_')[1] : ISIN;

  return `${base}-${fixedValue}${company}-${group}-${subProduct}-${secondFixedValue}${id}.${req.mediaType}`;
};
